import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from '../../../services/utils/snackBar.service';
import { HandlerService } from '../../../services/handler/handler.service';
import { FileService } from '../../../services/file.service';
import * as XLSX from 'xlsx';

interface Proceso {
  idProceso: number;
  idModulo: number;
  nombreProceso: string;
  descripcion: string;
  template: string[];
  validaciones: {
    [key: string]: (val: any) => boolean;
  };
}

@Component({
  selector: 'app-data-import-uploader',
  templateUrl: './data-import-uploader.component.html',
  styleUrls: ['./data-import-uploader.component.css']
})
export class DataImportUploaderComponent implements OnInit {
  constructor(
    public _dialog: MatDialog,
    private _snackBar: SnackBarService,
    private _datePipe: DatePipe,
    private _handlerService: HandlerService,
    private _fileService: FileService
  ) {}

  modulos = [{ idModulo: 1, nombreModulo: 'Tablero BI' }];

procesos: Proceso[] = [
  {
    idProceso: 1,
    idModulo: 1,
    nombreProceso: 'Datos Auxiliares - Prueba',
    descripcion: `Importación de datos auxiliares por período para análisis BI.
        Validaciones requeridas para el archivo Excel:
        - CUIL: debe tener 11 dígitos numéricos.
        - Periodo: formato numérico de 6 dígitos (AAAAMM).
        - Concepto: solo letras, números, espacios, guiones y barras (A-Z, 0-9, - /).
        - Cantidad: valor numérico válido.
        - Importe: valor numérico válido, puede tener separadores decimales y miles.`,
    template: ['CUIL', 'Periodo', 'Concepto', 'Cantidad', 'Importe'],
    validaciones: {
      CUIL: (val: string) => /^\d{11}$/.test(val),
      Periodo: (val: string) => /^\d{6}$/.test(val),
      Concepto: (val: string) => /^[A-Za-zÁÉÍÓÚÜÑ0-9\s\-\/]+$/.test(val),
      Cantidad: (val: any) => !isNaN(val),
      Importe: (val: any) => !isNaN(Number(String(val).replace(/[$.\s]/g, '').replace(',', '.')))
    }
  }
];


  procesosFiltrados: Proceso[] = [];
  selectedModuloId: any;
  selectedProcesoId: any;
  descripcionProceso: string = '';
  archivoSeleccionado: File | null = null;
  mensajeError: string = '';

  ngOnInit() {
    this.selectedModuloId = this.modulos[0].idModulo;
    this.procesosFiltrados = this.procesos.filter(p => p.idModulo === this.selectedModuloId);
    this.selectedProcesoId = this.procesosFiltrados[0].idProceso;
    this.descripcionProceso = this.procesosFiltrados[0].descripcion;
  }

  onModuloChange() {
    this.procesosFiltrados = this.procesos.filter(p => p.idModulo === Number(this.selectedModuloId));
    this.selectedProcesoId = 0;
    this.descripcionProceso = '';
  }

  private showToast(mensaje: string, tipo = 'success') {
    this._snackBar.openSnackBar(`snack-${tipo}`, mensaje, 3000);
    return true;
  }

  private getProcesoActual(): Proceso | undefined {
    return this.procesos.find(p => p.idProceso === Number(this.selectedProcesoId));
  }

  onProcesoChange() {
    const proceso = this.getProcesoActual();
    this.descripcionProceso = proceso?.descripcion || '';
  }

  descargarTemplate() {
    const proceso = this.getProcesoActual();
    if (!proceso) return;

    const ws = XLSX.utils.aoa_to_sheet([proceso.template]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'template.xlsx');
  }

  onFileSelected(event: any) {
    this.archivoSeleccionado = event.target.files[0];
    this.mensajeError = '';
  }

  async procesarArchivo() {
    if (!this.archivoSeleccionado) return;

    const buffer = await this.archivoSeleccionado.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'buffer' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as string[][];

    const proceso = this.getProcesoActual();
    if (!proceso) return;

    const headers = jsonData[0];
    const expectedHeaders = proceso.template;
    const registros = jsonData.slice(1);

    if (headers.join() !== expectedHeaders.join()) {
      this.mensajeError = 'Las columnas del archivo no coinciden con el template esperado.';
      return;
    }

    if (registros.length === 0) {
      this.mensajeError = 'El archivo no contiene registros.';
      return;
    }

    const errores: string[] = [];
    const combinaciones = new Set<string>();
    const idxPeriodo = expectedHeaders.indexOf('Periodo');
    const idxCuil = expectedHeaders.indexOf('CUIL');
    const idxConcepto = expectedHeaders.indexOf('Concepto');
    const primerPeriodo = registros[0][idxPeriodo];

    for (let i = 0; i < registros.length; i++) {
      const fila = registros[i];
      const filaNro = i + 2;

      if (!fila[0]) continue; // Saltear filas vacías (sin CUIL)

      for (let j = 0; j < expectedHeaders.length; j++) {
        const campo = expectedHeaders[j];
        const valor = fila[j];

        if (valor === undefined || valor === null || valor === '') {
          errores.push(`Fila ${filaNro}: El campo "${campo}" está vacío.`);
          continue;
        }

        const validFn = proceso.validaciones[campo];
        if (validFn && !validFn(valor)) {
          errores.push(`Fila ${filaNro}: El campo "${campo}" tiene un valor inválido: ${valor}`);
        }
      }

      if (fila[idxPeriodo] !== primerPeriodo) {
        errores.push(`Fila ${filaNro}: El período ${fila[idxPeriodo]} no coincide con el primero (${primerPeriodo}).`);
      }

      const cuil = fila[idxCuil];
      if (!/^\d{11}$/.test(String(cuil))) {
        errores.push(`Fila ${filaNro}: CUIL inválido: ${cuil}`);
      }

      const clave = `${fila[idxCuil]}-${fila[idxPeriodo]}-${fila[idxConcepto]}`;
      if (combinaciones.has(clave)) {
        errores.push(`Fila ${filaNro}: Registro duplicado CUIL-PERIODO-CONCEPTO: ${clave}`);
      } else {
        combinaciones.add(clave);
      }
    }

    if (errores.length > 0) {
      this.mensajeError = errores.join('\n');
      return;
    }

    this.mensajeError = '';

    const listaJson = registros
      .filter(f => f[0])
      .map(fila => {
        const obj: { [key: string]: any } = {};
        expectedHeaders.forEach((key, idx) => {
          obj[key] = fila[idx];
        });
        return obj;
      });

    const params = {
      pProceso: 'TabBiImportCostoLaboral_Sueldos',
      pIdUsuario: 0,
      pData: JSON.stringify(listaJson)
    };

    this._handlerService.postData(JSON.stringify(params)).subscribe({
      next: (res: any) => {
        this.showToast(res.mensaje, res.result === 1 ? 'success' : 'danger');
        if (res.result === 1) {
          console.log('Respuesta OK:', res.data);
        }
      },
      error: (err) => {
        console.error('Error al enviar consulta', err);
        this.showToast('Ocurrió un error al enviar la consulta.', 'danger');
      }
    });
  }

  testHandler() {
    const params = JSON.stringify({
      pProceso: 'PingTest',
      pIdSancion: 1,
      pIdEstado: 0,
      pIdUsuario: 0
    });

    this._handlerService.postData(params).subscribe({
      next: (res: any) => {
        this.showToast(res.mensaje, res.result === 1 ? 'success' : 'danger');
        if (res.result === 1) {
          console.log('Respuesta OK:', res.data);
        }
      },
      error: (err) => {
        console.error('Error al enviar consulta', err);
        this.showToast('Ocurrió un error al enviar la consulta.', 'danger');
      }
    });
  }
}
